import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import * as Icons from 'lucide-react';

/*
 * Единая страница «Правовая информация»: все документы в одном месте.
 * Заменяет длинный список правовых ссылок в футере (как на serva.one).
 */

const DOCS = {
  ru: [
    { to: '/terms', icon: 'FileText', title: 'Условия использования', text: 'Публичная оферта: правила пользования услугами и сайтом, права и обязанности сторон.' },
    { to: '/privacy', icon: 'Lock', title: 'Политика конфиденциальности', text: 'Какие персональные данные мы собираем, зачем они нужны, как защищаются и когда передаются.' },
    { to: '/refund', icon: 'RotateCcw', title: 'Политика возврата средств', text: 'Гарантия возврата в первые дни, порядок подачи заявки и условия расчёта суммы.' },
    { to: '/affiliate-terms', icon: 'Users', title: 'Правила партнёрской программы', text: 'Как начисляется и выплачивается вознаграждение, что запрещено и когда партнёрство прекращается.' },
    { to: '/cookie', icon: 'Cookie', title: 'Политика Cookie', text: 'Что мы храним в вашем браузере, для чего это нужно и как это отключить.' },
    { to: '/compliance', icon: 'Scale', title: 'Санкционный комплаенс', text: 'Как мы соблюдаем санкционные режимы Великобритании, ЕС, США и ООН и каким юрисдикциям не оказываем услуги.' },
  ],
  en: [
    { to: '/terms', icon: 'FileText', title: 'Terms of Service', text: 'The public offer: rules for using our services and website, rights and obligations of both sides.' },
    { to: '/privacy', icon: 'Lock', title: 'Privacy Policy', text: 'What personal data we collect, why we need it, how we protect it and when we share it.' },
    { to: '/refund', icon: 'RotateCcw', title: 'Refund Policy', text: 'The money-back guarantee, how to request a refund and how the amount is calculated.' },
    { to: '/affiliate-terms', icon: 'Users', title: 'Affiliate Program Rules', text: 'How commission is earned and paid, what promotion is not allowed and how the partnership can end.' },
    { to: '/cookie', icon: 'Cookie', title: 'Cookie Policy', text: 'What we store in your browser, why we need it and how to turn it off.' },
    { to: '/compliance', icon: 'Scale', title: 'Sanctions Compliance', text: 'How we comply with UK, EU, US and UN sanctions regimes, and the jurisdictions we cannot serve.' },
  ],
};

const T = {
  ru: {
    title: 'Правовая информация',
    subtitle: 'Всё, что регулирует нашу работу с вами, собрано в одном месте. Эти документы распространяются на все услуги NetGrid Host Ltd.',
    abuseTitle: 'Сообщить о злоупотреблении',
    abuseText: 'Если с нашего адреса идёт спам, атака или иная противоправная активность — напишите нам. Мы рассматриваем такие обращения в приоритетном порядке.',
    abuseBtn: 'Написать в отдел злоупотреблений',
    foot: 'Остались вопросы по документам?',
    footLink: 'Свяжитесь с нами',
    seoTitle: 'Правовая информация | NetGrid Host',
    seoDesc: 'Все правовые документы NetGrid Host Ltd в одном месте: условия использования, конфиденциальность, возврат средств, cookie, правила партнёрской программы и санкционный комплаенс.',
  },
  en: {
    title: 'Legal Information',
    subtitle: 'Everything that governs our work with you, in one place. These documents apply to all services provided by NetGrid Host Ltd.',
    abuseTitle: 'Report Abuse',
    abuseText: 'If spam, an attack or any other unlawful activity originates from one of our addresses, tell us. Such reports are handled as a priority.',
    abuseBtn: 'Contact the abuse desk',
    foot: 'Questions about any of these documents?',
    footLink: 'Contact us',
    seoTitle: 'Legal Information | NetGrid Host',
    seoDesc: 'All legal documents of NetGrid Host Ltd in one place: terms of service, privacy policy, refund policy, cookie policy, affiliate program rules and sanctions compliance.',
  },
};

const Ico = ({ name, className }) => {
  const C = Icons[name] || Icons.FileText;
  return <C className={className} />;
};

export const LegalHubPage = () => {
  const { pathname } = useLocation();
  const ru = pathname.startsWith('/ru');
  const lang = ru ? 'ru' : 'en';
  const t = T[lang];
  const docs = DOCS[lang];
  const lp = (p) => (ru ? `/ru${p}` : p);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="legal" />
      <Header />

      <section className="bg-gradient-to-b from-slate-900 to-slate-800 pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-500/10 border border-blue-500/20 mb-6">
            <Icons.Scale className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t.title}</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {docs.map((d, i) => (
              <Link
                key={i}
                to={lp(d.to)}
                className="group block bg-white border border-slate-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-lg transition-all duration-300"
              >
                <h2 className="flex items-center gap-3 text-lg font-semibold text-slate-900 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
                    <Ico name={d.icon} className="w-5 h-5" />
                  </span>
                  {d.title}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">{d.text}</p>
              </Link>
            ))}
          </div>

          <div className="mt-10 bg-slate-50 border border-slate-200 rounded-2xl p-6 sm:p-8">
            <h2 className="flex items-center gap-3 text-lg font-semibold text-slate-900 mb-2">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-amber-50 text-amber-600">
                <Icons.AlertTriangle className="w-5 h-5" />
              </span>
              {t.abuseTitle}
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">{t.abuseText}</p>
            <a
              href="mailto:abuse-report@netgrid.host"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              <Icons.Mail className="w-4 h-4" />
              {t.abuseBtn}
            </a>
          </div>

          <p className="text-center text-sm text-slate-500 mt-10">
            {t.foot}{' '}
            <Link to={lp('/support')} className="text-blue-600 hover:text-blue-700 underline">
              {t.footLink}
            </Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};
